import React, { useState, useEffect } from 'react';
import { Card, Button, Input, Label } from '../ui';
import { useApp } from '../../contexts/AppContext';
import {
  getContactableUsers,
  findOrCreateConversation,
  sendMessage,
  ContactableUser
} from '../../services/messagingService';
import { MessageChannel } from '../../types';

interface ComposeMessageProps {
  onConversationCreated: (conversationId: string) => void;
  onCancel: () => void;
}

const CHANNEL_OPTIONS: { value: MessageChannel; label: string; icon: React.ReactNode }[] = [
  {
    value: 'whatsapp',
    label: 'WhatsApp',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    )
  },
  {
    value: 'sms',
    label: 'SMS',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    )
  },
  {
    value: 'email',
    label: 'Email',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    )
  }
];

const ComposeMessage: React.FC<ComposeMessageProps> = ({
  onConversationCreated,
  onCancel
}) => {
  const { selectedOrganization } = useApp();
  const [channel, setChannel] = useState<MessageChannel>('whatsapp');
  const [recipients, setRecipients] = useState<ContactableUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRecipient, setSelectedRecipient] = useState<ContactableUser | null>(null);
  const [messageText, setMessageText] = useState('');
  const [subject, setSubject] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'player' | 'guardian'>('all');

  // Load users based on selected channel
  useEffect(() => {
    if (!selectedOrganization?.id) return;

    setLoading(true);
    setSelectedRecipient(null);
    setSearchQuery('');
    getContactableUsers(selectedOrganization.id, channel)
      .then(setRecipients)
      .catch((err) => {
        console.error('Error loading recipients:', err);
        setError('Failed to load recipients');
      })
      .finally(() => setLoading(false));
  }, [selectedOrganization?.id, channel]);

  const filteredRecipients = recipients.filter(r => {
    const contactField = channel === 'email' ? (r.email || '') : (r.phone || '');
    const matchesSearch = searchQuery === '' ||
      r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contactField.includes(searchQuery);
    const matchesType = filterType === 'all' || r.type === filterType;
    return matchesSearch && matchesType;
  });

  const handleSend = async () => {
    if (!selectedOrganization?.id || !selectedRecipient || !messageText.trim()) return;
    if (channel === 'email' && !subject.trim()) {
      setError('Subject is required for email messages');
      return;
    }

    setSending(true);
    setError(null);

    try {
      const conversation = await findOrCreateConversation(
        selectedOrganization.id,
        selectedRecipient.id,
        selectedRecipient.name,
        selectedRecipient.type,
        channel,
        { phone: selectedRecipient.phone, email: selectedRecipient.email }
      );

      const result = await sendMessage(
        conversation.id,
        messageText.trim(),
        selectedOrganization.id,
        channel,
        channel === 'email' ? { subject: subject.trim() } : undefined
      );

      if (result.success) {
        onConversationCreated(conversation.id);
      } else {
        setError(result.error || 'Failed to send message');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create conversation');
    } finally {
      setSending(false);
    }
  };

  const getContactDisplay = (r: ContactableUser) => {
    return channel === 'email' ? r.email : r.phone;
  };

  const emptyStateText = channel === 'email'
    ? 'No users with an email address'
    : `No users with a phone number for ${channel === 'whatsapp' ? 'WhatsApp' : 'SMS'}`;

  return (
    <Card className="h-full">
      {/* Header */}
      <div className="p-4 border-b border-secondary-200 flex items-center justify-between">
        <h3 className="font-semibold text-secondary-900">New Message</h3>
        <button
          onClick={onCancel}
          className="p-2 hover:bg-secondary-100 rounded-lg"
        >
          <svg className="w-5 h-5 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="p-4">
        {/* Channel Selector */}
        <div className="flex gap-2 mb-4">
          {CHANNEL_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setChannel(opt.value)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                channel === opt.value
                  ? 'bg-primary-100 text-primary-700 ring-2 ring-primary-500'
                  : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
              }`}
            >
              {opt.icon}
              {opt.label}
            </button>
          ))}
        </div>

        {/* Recipient Selection */}
        {!selectedRecipient ? (
          <div className="space-y-4">
            {/* Search and filter */}
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <Input
                  type="text"
                  placeholder={channel === 'email' ? 'Search by name or email...' : 'Search by name or phone...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as 'all' | 'player' | 'guardian')}
                className="px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">All</option>
                <option value="player">Players</option>
                <option value="guardian">Guardians</option>
              </select>
            </div>

            {/* Recipients list */}
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : filteredRecipients.length === 0 ? (
              <div className="text-center py-8">
                <svg className="w-12 h-12 text-secondary-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="text-secondary-500">
                  {searchQuery ? 'No matching recipients found' : emptyStateText}
                </p>
              </div>
            ) : (
              <div className="max-h-[300px] overflow-y-auto space-y-2">
                {filteredRecipients.map((recipient) => (
                  <div
                    key={recipient.id}
                    onClick={() => setSelectedRecipient(recipient)}
                    className="p-3 border border-secondary-200 rounded-lg cursor-pointer hover:bg-secondary-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`
                        w-10 h-10 rounded-full flex items-center justify-center font-semibold
                        ${recipient.type === 'player'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-purple-100 text-purple-700'
                        }
                      `}>
                        {recipient.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-secondary-900">{recipient.name}</p>
                        <p className="text-sm text-secondary-500">{getContactDisplay(recipient)}</p>
                      </div>
                      <span className={`
                        px-2 py-0.5 text-xs rounded-full
                        ${recipient.type === 'player'
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-purple-50 text-purple-700'
                        }
                      `}>
                        {recipient.type === 'player' ? 'Player' : 'Guardian'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Message Composition */
          <div className="space-y-4">
            {/* Selected recipient */}
            <div className="flex items-center gap-3 p-3 bg-secondary-50 rounded-lg">
              <div className={`
                w-10 h-10 rounded-full flex items-center justify-center font-semibold
                ${selectedRecipient.type === 'player'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-purple-100 text-purple-700'
                }
              `}>
                {selectedRecipient.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1">
                <p className="font-medium text-secondary-900">{selectedRecipient.name}</p>
                <p className="text-sm text-secondary-500">{getContactDisplay(selectedRecipient)}</p>
              </div>
              <button
                onClick={() => setSelectedRecipient(null)}
                className="p-1 hover:bg-secondary-200 rounded"
              >
                <svg className="w-4 h-4 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Email subject */}
            {channel === 'email' && (
              <div>
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter email subject..."
                />
              </div>
            )}

            {/* Message input */}
            <div>
              <Label htmlFor="message">Message</Label>
              <textarea
                id="message"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Type your message..."
                rows={4}
                className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Error */}
            {error && (
              <div className="p-3 bg-error-50 border border-error-200 rounded-lg">
                <p className="text-sm text-error-700">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setSelectedRecipient(null)}
              >
                Back
              </Button>
              <Button
                onClick={handleSend}
                disabled={sending || !messageText.trim() || (channel === 'email' && !subject.trim())}
              >
                {sending ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    Send Message
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default ComposeMessage;
